import React, { useState, useEffect } from 'react';
import { apiPost, apiGet } from '../utils/api';
import { Code, Hash, Shield, Database, FileText, Copy, RefreshCw, Check, X, GitBranch, BarChart3 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useLocation, Link as RouterLink, Routes, Route, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import BenchmarkBuilder from './development/BenchmarkBuilder';
import GitCheatsheet from './development/GitCheatsheet';

// Função utilitária para obter categoria e nome da ferramenta ativa
export function getActiveToolInfo(location) {
  const tabs = [
    { id: 'css', name: 'Gerador de CSS', path: '/development/css' },
    { id: 'regex', name: 'Validador de Regex', path: '/development/regex' },
    { id: 'api-mock', name: 'Gerador de API Mock', path: '/development/api-mock' },
    { id: 'sql', name: 'Gerador de SQL', path: '/development/sql' },
    { id: 'uuid', name: 'Gerador de UUID', path: '/development/uuid' },
    { id: 'password', name: 'Senha Forte', path: '/development/password' },
    { id: 'git-cheatsheet', name: 'Git Cheatsheet', path: '/development/git-cheatsheet' },
    { id: 'benchmark-builder', name: 'Construtor de Benchmark', path: '/development/benchmark-builder' }
  ];
  const match = tabs.find(tab => location.pathname.startsWith(tab.path));
  return { category: 'Desenvolvimento', tool: match ? match.name : null };
}

// Função utilitária para copiar texto para a área de transferência
const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
  toast.success('Copiado para a área de transferência!');
};

const Development = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Definição das abas e rotas
  const tabs = [
    { id: 'css', name: 'Gerador de CSS', icon: Code, path: '/development/css', description: 'CSS personalizado', status: 'active' },
    { id: 'regex', name: 'Validador de Regex', icon: FileText, path: '/development/regex', description: 'Teste de expressões', status: 'active' },
    { id: 'api-mock', name: 'Gerador de API Mock', icon: Code, path: '/development/api-mock', description: 'APIs de teste', status: 'active' },
    { id: 'sql', name: 'Gerador de SQL', icon: Database, path: '/development/sql', description: 'Queries SQL', status: 'active' },
    { id: 'uuid', name: 'Gerador de UUID', icon: Hash, path: '/development/uuid', description: 'UUIDs únicos', status: 'active' },
    { id: 'password', name: 'Senha Forte', icon: Shield, path: '/development/password', description: 'Senhas seguras', status: 'active' },
    { id: 'git-cheatsheet', name: 'Git Cheatsheet', icon: GitBranch, path: '/development/git-cheatsheet', description: 'Comandos Git comuns', status: 'active' },
    { id: 'benchmark-builder', name: 'Construtor de Benchmark', icon: BarChart3, path: '/development/benchmark-builder', description: 'Teste performance de código', status: 'active' }
  ];

  // Redirecionar /development para /development/css
  useEffect(() => {
    if (location.pathname === '/development') {
      navigate('/development/css');
    }
  }, [location.pathname, navigate]);

  return (
    <div className="p-6 max-w-6xl mx-auto w-full overflow-x-hidden bg-white min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Ferramentas de Desenvolvimento
        </h1>
        <p className="text-gray-600">
          Geradores de código e validadores para desenvolvedores
        </p>
      </div>

      {/* Navegação rápida das ferramentas */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Ferramentas Disponíveis</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {tabs.filter(tab => tab.status === 'active').map(tab => (
            <RouterLink
              key={tab.id}
              to={tab.path}
              className={`p-4 bg-white border rounded-lg transition-all duration-200 flex items-center space-x-3 ${location.pathname === tab.path ? 'border-blue-500 bg-blue-50 shadow' : 'border-gray-200 hover:shadow-md hover:border-blue-300'}`}
            >
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <tab.icon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 text-base md:text-[15px] lg:text-base leading-tight break-words whitespace-normal">{tab.name}</h3>
                <p className="text-sm text-gray-500 leading-snug break-words whitespace-normal">{tab.description}</p>
              </div>
            </RouterLink>
          ))}
        </div>
      </div>

      <Routes>
        <Route path="css" element={<CSSGenerator />} />
        <Route path="regex" element={<RegexValidator />} />
        <Route path="api-mock" element={<APIMockGenerator />} />
        <Route path="sql" element={<SQLGenerator />} />
        <Route path="uuid" element={<UUIDGenerator />} />
        <Route path="password" element={<StrongPasswordGenerator />} />
        <Route path="git-cheatsheet" element={<GitCheatsheet />} />
        <Route path="benchmark-builder" element={<BenchmarkBuilder />} />
      </Routes>
    </div>
  );
};

// Componente Gerador de CSS
const CSSGenerator = () => {
  const [cssType, setCssType] = useState('button');
  const [properties, setProperties] = useState({});
  const [generatedCSS, setGeneratedCSS] = useState('');

  const cssTemplates = {
    button: {
      backgroundColor: '#3B82F6',
      color: '#FFFFFF',
      padding: '12px 24px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    card: {
      backgroundColor: '#FFFFFF',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
    },
    input: {
      border: '1px solid #D1D5DB',
      borderRadius: '6px',
      padding: '12px',
      fontSize: '16px',
      width: '100%'
    },
    modal: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: '#FFFFFF',
      padding: '32px',
      borderRadius: '8px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
      zIndex: '1000'
    }
  };

  useEffect(() => {
    setProperties(cssTemplates[cssType]);
  }, [cssType]);

  const generateCSS = () => {
    const cssProperties = Object.entries(properties)
      .map(([property, value]) => `  ${property}: ${value};`)
      .join('\n');
    
    const css = `.${cssType} {\n${cssProperties}\n}`;
    setGeneratedCSS(css);
  };

  const updateProperty = (property, value) => {
    setProperties(prev => ({
      ...prev,
      [property]: value
    }));
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Tipo de Componente
          </label>
          <select
            value={cssType}
            onChange={(e) => setCssType(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="button">Botão</option>
            <option value="card">Card</option>
            <option value="input">Input</option>
            <option value="modal">Modal</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-4">Propriedades CSS</h4>
            <div className="space-y-3">
              {Object.entries(properties).map(([property, value]) => (
                <div key={property}>
                  <label className="block text-sm text-gray-600 mb-1">
                    {property}
                  </label>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => updateProperty(property, e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-4">CSS Gerado</h4>
            <textarea
              value={generatedCSS}
              readOnly
              className="w-full h-64 p-3 border border-gray-300 rounded-lg bg-gray-50 resize-none font-mono text-sm"
              placeholder="O CSS será gerado aqui..."
            />
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={generateCSS}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Gerar CSS
          </button>

          {generatedCSS && (
            <button
              onClick={() => copyToClipboard(generatedCSS)}
              className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              <Copy size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente Validador de Regex
const RegexValidator = () => {
  const [pattern, setPattern] = useState('');
  const [testString, setTestString] = useState('');
  const [flags, setFlags] = useState('g');
  const [results, setResults] = useState(null);

  const validateRegex = () => {
    if (!pattern.trim()) {
      toast.error('Digite um padrão regex');
      return;
    }

    try {
      const regex = new RegExp(pattern, flags);
      const matches = testString.match(regex);
      const isValid = regex.test(testString);
      
      setResults({
        isValid,
        matches: matches || [],
        error: null
      });
    } catch (error) {
      setResults({
        isValid: false,
        matches: [],
        error: error.message
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Padrão Regex
            </label>
            <input
              type="text"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="[a-zA-Z]+"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Flags
            </label>
            <input
              type="text"
              value={flags}
              onChange={(e) => setFlags(e.target.value)}
              placeholder="g, i, m, s, u, y"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            String de Teste
          </label>
          <textarea
            value={testString}
            onChange={(e) => setTestString(e.target.value)}
            placeholder="Digite o texto para testar o regex..."
            className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <button
          onClick={validateRegex}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Validar Regex
        </button>

        {results && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className={`flex items-center space-x-2 mb-4 ${
              results.error ? 'text-red-600' : 
              results.isValid ? 'text-green-600' : 'text-red-600'
            }`}>
              {results.error ? (
                <X className="text-red-500" size={20} />
              ) : results.isValid ? (
                <Check className="text-green-500" size={20} />
              ) : (
                <X className="text-red-500" size={20} />
              )}
              <span className="font-medium">
                {results.error ? 'Regex Inválido' : 
                 results.isValid ? 'Regex Válido - Encontrou Matches' : 'Regex Válido - Nenhum Match'}
              </span>
            </div>

            {results.error && (
              <div className="text-sm text-red-600 mb-4">
                Erro: {results.error}
              </div>
            )}

            {results.matches.length > 0 && (
              <div>
                <div className="text-sm font-medium text-gray-900 mb-2">
                  Matches Encontrados ({results.matches.length}):
                </div>
                <div className="space-y-1">
                  {results.matches.map((match, index) => (
                    <div key={index} className="text-sm font-mono bg-white p-2 rounded border">
                      {match}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Gerador de API Mock
const APIMockGenerator = () => {
  const [endpoint, setEndpoint] = useState('/api/users');
  const [method, setMethod] = useState('GET');
  const [responseType, setResponseType] = useState('users');
  const [generatedMock, setGeneratedMock] = useState('');

  const mockTemplates = {
    users: {
      data: [
        { id: 1, name: 'John Doe', email: 'john@example.com', role: 'admin' },
        { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'user' },
        { id: 3, name: 'Bob Johnson', email: 'bob@example.com', role: 'user' }
      ]
    },
    products: {
      data: [
        { id: 1, name: 'Product 1', price: 29.99, category: 'electronics' },
        { id: 2, name: 'Product 2', price: 49.99, category: 'clothing' },
        { id: 3, name: 'Product 3', price: 19.99, category: 'books' }
      ]
    },
    posts: {
      data: [
        { id: 1, title: 'First Post', content: 'This is the first post', author: 'John' },
        { id: 2, title: 'Second Post', content: 'This is the second post', author: 'Jane' },
        { id: 3, title: 'Third Post', content: 'This is the third post', author: 'Bob' }
      ]
    }
  };

  const generateMock = () => {
    const template = mockTemplates[responseType];
    const mockData = {
      success: true,
      message: 'Data retrieved successfully',
      ...template
    };

    const mockCode = `// Mock API Response for ${method} ${endpoint}
const mockResponse = ${JSON.stringify(mockData, null, 2)};

// Express.js route example
app.${method.toLowerCase()}('${endpoint}', (req, res) => {
  res.json(mockResponse);
});

// Fetch example
fetch('${endpoint}', {
  method: '${method}',
  headers: {
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => console.log(data));`;

    setGeneratedMock(mockCode);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Endpoint
            </label>
            <input
              type="text"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder="/api/users"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Método HTTP
            </label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Tipo de Resposta
            </label>
            <select
              value={responseType}
              onChange={(e) => setResponseType(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="users">Usuários</option>
              <option value="products">Produtos</option>
              <option value="posts">Posts</option>
            </select>
          </div>
        </div>

        <button
          onClick={generateMock}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Gerar Mock API
        </button>

        {generatedMock && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">Código Gerado</h4>
              <button
                onClick={() => copyToClipboard(generatedMock)}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <Copy size={16} />
              </button>
            </div>
            <textarea
              value={generatedMock}
              readOnly
              className="w-full h-64 p-3 border border-gray-300 rounded-lg bg-gray-50 resize-none font-mono text-sm"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Gerador de SQL
const SQLGenerator = () => {
  const [tableName, setTableName] = useState('users');
  const [columns, setColumns] = useState([
    { name: 'id', type: 'INT', primary: true, autoIncrement: true },
    { name: 'name', type: 'VARCHAR(255)', primary: false, autoIncrement: false },
    { name: 'email', type: 'VARCHAR(255)', primary: false, autoIncrement: false },
    { name: 'created_at', type: 'TIMESTAMP', primary: false, autoIncrement: false }
  ]);
  const [generatedSQL, setGeneratedSQL] = useState('');

  const addColumn = () => {
    setColumns([...columns, { name: '', type: 'VARCHAR(255)', primary: false, autoIncrement: false }]);
  };

  const removeColumn = (index) => {
    setColumns(columns.filter((_, i) => i !== index));
  };

  const updateColumn = (index, field, value) => {
    const newColumns = [...columns];
    newColumns[index][field] = value;
    setColumns(newColumns);
  };

  const generateSQL = () => {
    const columnDefinitions = columns
      .filter(col => col.name.trim())
      .map(col => {
        let definition = `  ${col.name} ${col.type}`;
        if (col.primary) definition += ' PRIMARY KEY';
        if (col.autoIncrement) definition += ' AUTO_INCREMENT';
        return definition;
      })
      .join(',\n');

    const sql = `CREATE TABLE ${tableName} (\n${columnDefinitions}\n);`;
    setGeneratedSQL(sql);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Nome da Tabela
          </label>
          <input
            type="text"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            placeholder="users"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">Colunas</h4>
            <button
              onClick={addColumn}
              className="bg-green-600 text-white py-1 px-3 rounded text-sm hover:bg-green-700 transition-colors"
            >
              Adicionar Coluna
            </button>
          </div>

          <div className="space-y-3">
            {columns.map((column, index) => (
              <div key={index} className="grid grid-cols-4 gap-3 items-center">
                <input
                  type="text"
                  value={column.name}
                  onChange={(e) => updateColumn(index, 'name', e.target.value)}
                  placeholder="Nome da coluna"
                  className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <select
                  value={column.type}
                  onChange={(e) => updateColumn(index, 'type', e.target.value)}
                  className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="INT">INT</option>
                  <option value="VARCHAR(255)">VARCHAR(255)</option>
                  <option value="TEXT">TEXT</option>
                  <option value="TIMESTAMP">TIMESTAMP</option>
                  <option value="BOOLEAN">BOOLEAN</option>
                  <option value="DECIMAL(10,2)">DECIMAL(10,2)</option>
                </select>
                <div className="flex items-center space-x-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={column.primary}
                      onChange={(e) => updateColumn(index, 'primary', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-1 text-sm text-gray-600">PK</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={column.autoIncrement}
                      onChange={(e) => updateColumn(index, 'autoIncrement', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-1 text-sm text-gray-600">AI</span>
                  </label>
                </div>
                <button
                  onClick={() => removeColumn(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={generateSQL}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Gerar SQL
        </button>

        {generatedSQL && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">SQL Gerado</h4>
              <button
                onClick={() => copyToClipboard(generatedSQL)}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <Copy size={16} />
              </button>
            </div>
            <textarea
              value={generatedSQL}
              readOnly
              className="w-full h-32 p-3 border border-gray-300 rounded-lg bg-gray-50 resize-none font-mono text-sm"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Gerador de UUID
const UUIDGenerator = () => {
  const [uuids, setUuids] = useState([]);
  const [count, setCount] = useState(5);

  const generateUUIDs = () => {
    const newUuids = Array.from({ length: count }, () => uuidv4());
    setUuids(newUuids);
  };

  const generateSingleUUID = () => {
    const newUuid = uuidv4();
    setUuids([newUuid]);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Quantidade
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="w-24 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <button
            onClick={generateUUIDs}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Gerar {count} UUIDs
          </button>

          <button
            onClick={generateSingleUUID}
            className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors duration-200"
          >
            Gerar 1 UUID
          </button>
        </div>

        {uuids.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">
                UUIDs Gerados ({uuids.length})
              </h4>
              <button
                onClick={() => copyToClipboard(uuids.join('\n'))}
                className="bg-gray-600 text-white py-1 px-3 rounded text-sm hover:bg-gray-700 transition-colors"
              >
                <Copy size={14} />
              </button>
            </div>

            <div className="space-y-2">
              {uuids.map((uuid, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <code className="text-sm font-mono text-gray-900">
                    {uuid}
                  </code>
                  <button
                    onClick={() => copyToClipboard(uuid)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Gerador de Senha Forte
const StrongPasswordGenerator = () => {
  const [password, setPassword] = useState('');
  const [length, setLength] = useState(16);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [excludeSimilar, setExcludeSimilar] = useState(true);

  const generatePassword = () => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const similar = 'il1Lo0O';

    let chars = '';
    if (includeUppercase) chars += uppercase;
    if (includeLowercase) chars += lowercase;
    if (includeNumbers) chars += numbers;
    if (includeSymbols) chars += symbols;

    if (excludeSimilar) {
      chars = chars.split('').filter(char => !similar.includes(char)).join('');
    }

    if (chars.length === 0) {
      toast.error('Selecione pelo menos um tipo de caractere');
      return;
    }

    let generatedPassword = '';
    for (let i = 0; i < length; i++) {
      generatedPassword += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    setPassword(generatedPassword);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Comprimento: {length} caracteres
          </label>
          <input
            type="range"
            min="8"
            max="64"
            value={length}
            onChange={(e) => setLength(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeUppercase}
                onChange={(e) => setIncludeUppercase(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-900">Incluir maiúsculas (A-Z)</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeLowercase}
                onChange={(e) => setIncludeLowercase(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-900">Incluir minúsculas (a-z)</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeNumbers}
                onChange={(e) => setIncludeNumbers(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-900">Incluir números (0-9)</span>
            </label>
          </div>

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeSymbols}
                onChange={(e) => setIncludeSymbols(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-900">Incluir símbolos (!@#$%^&*)</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={excludeSimilar}
                onChange={(e) => setExcludeSimilar(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-900">Excluir caracteres similares (il1Lo0O)</span>
            </label>
          </div>
        </div>

        <button
          onClick={generatePassword}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Gerar Senha
        </button>

        {password && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">Senha Gerada</span>
              <button
                onClick={() => copyToClipboard(password)}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <Copy size={16} />
              </button>
            </div>
            <div className="text-lg font-mono text-gray-900 break-all">
              {password}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Development; 